/*
 * Runtime squircle engine — gives every matched container/button the emoji-avatar
 * superellipse corner WITH a working border, driven by ONE pair of design tokens,
 * and survives React re-renders.
 *
 * ─── DESIGN TOKENS — they live in design-system.js, NOT here ────────────────
 *   `SQUIRCLE` is imported below; edit it THERE to restyle every corner
 *   app-wide. This file used to keep its own copy, which is how the corner
 *   tokens quietly stopped matching the design system.
 *   TOKENS.container / TOKENS.button each carry:
 *     n = smoothness (superellipse exponent; higher = more "squircle")
 *     r = roundness  (corner radius in px)
 *   The element's OWN inline border-radius only decides *whether* it opts in
 *   (any px radius > 0 = yes; 50%/percent = no, it's a circle). The actual shape
 *   comes from its token, chosen by whether it's a button or a container — so
 *   EVERY button looks identical and EVERY container looks identical.
 *
 * Why runtime JS (not CSS): a CSS mask/clip cuts the painted box, but a square-
 * cornered CSS border has no paint along the squircle arc — so every pure-CSS
 * attempt lost the border at the corners. The emoji tile works because its border
 * is an SVG STROKE on the same path. This generalizes that per element:
 *   1. clip-path: path() clips bg/content to an exact-size superellipse,
 *   2. blank the native border (color→transparent, width kept for layout, radius 0),
 *   3. inject an abs-positioned SVG that strokes the same path with the border color,
 *   4. glow box-shadow → filter:drop-shadow so it follows the curve.
 * The SVG sits exactly over the border box (left:0/top:0, size = rect) with its
 * stroke inset 0.6px, so ALL four sides stay inside the clip (a -bw offset used to
 * push the left/top strokes outside the clip → they vanished).
 *
 * SURVIVING REACT (the recurring "border disappears" bug):
 *   - React reconciles children on re-render; swapping a button's children
 *     (spinner↔label, a count appearing) can DELETE our injected SVG → a childList
 *     MutationObserver RE-APPENDS it.
 *   - When a tab's selected state flips its border COLOR, React rewrites the inline
 *     border. The style-mutation handler MUST re-sync EVERY managed element (the old
 *     code skipped elements that already had an SVG → selected tabs kept the old
 *     color and looked borderless). syncBorder re-reads the wanted color BEFORE
 *     blanking it and restrokes. All writes run in the MutationObserver microtask
 *     (pre-paint), so the native square border never flashes.
 *   - The stroke sits 0.6px inside the clip so :active scaling can't AA-nibble it.
 *
 * Skipped: percentages (circles), pills (radius ≥ half the short side → left native
 * so round icon-buttons stay round), replaced elements (can't hold the SVG), and
 * elements whose 4 border sides aren't identical (e.g. dashboard top-accent cards).
 */

import { LOW_END } from "./device.js";
import { SQUIRCLE } from "./design-system.js";

const SVG_NS = "http://www.w3.org/2000/svg";
const SKIP_TAGS = new Set(["SELECT", "INPUT", "TEXTAREA", "IMG", "VIDEO", "AUDIO", "CANVAS", "IFRAME", "HR", "BR", "SVG", "PATH"]);
const TRANSPARENT = new Set(["rgba(0, 0, 0, 0)", "transparent", ""]);

// Imported, never copied — a private duplicate here is exactly how "edit the
// tokens in one place" stopped being true for corners.
const TOKENS = SQUIRCLE;

function isButton(el) {
  const t = el.tagName;
  if (t === "BUTTON" || t === "A") return true;
  const role = el.getAttribute && el.getAttribute("role");
  return role === "button" || role === "tab";
}
function tokenFor(el) { return isButton(el) ? TOKENS.button : TOKENS.container; }

// The uniform corner radius from an inline border-radius, or null to skip.
// Skips: none / 0, percentages (circles), calc(), and — importantly — ANY element
// whose 4 corners aren't equal (e.g. tabs with "14px 14px 0 0"): those are an
// intentional custom shape (rounded-top tab) and must stay native & consistent,
// never squircled into a full rounded rect. Only used to decide opt-in + pill test.
function origRadius(el) {
  const br = el.style.borderRadius;
  if (!br || br.includes("%") || br.includes("(") || br.includes("/")) return null;
  const parts = br.trim().split(/\s+/).map(p => parseFloat(p));
  if (!parts.length || parts.some(v => Number.isNaN(v))) return null;
  const max = Math.max(...parts), min = Math.min(...parts);
  if (max - min > 0.5) return null;                 // non-uniform corners → skip
  return max > 0 ? max : null;
}

// A uniform (single radius r, exponent n) superellipse path, optionally inset.
function pathFor(w, h, r, n, inset) {
  const e = 2 / n, steps = 14;
  const x0 = inset, y0 = inset, x1 = w - inset, y1 = h - inset;
  const rr = Math.max(0, Math.min(r - inset, Math.min(x1 - x0, y1 - y0) / 2));
  const pts = [];
  const corner = (cx, cy, fx, fy, cosX) => {
    if (rr <= 0.1) return;
    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * (Math.PI / 2);
      const s = Math.pow(Math.sin(t), e), c = Math.pow(Math.cos(t), e);
      pts.push(`L${(cx + fx * rr * (cosX ? c : s)).toFixed(2)} ${(cy + fy * rr * (cosX ? s : c)).toFixed(2)}`);
    }
  };
  pts.push(`M${x0 + rr} ${y0}`, `L${x1 - rr} ${y0}`);
  corner(x1 - rr, y0 + rr, 1, -1, false);
  pts.push(`L${x1} ${y1 - rr}`);
  corner(x1 - rr, y1 - rr, 1, 1, true);
  pts.push(`L${x0 + rr} ${y1}`);
  corner(x0 + rr, y1 - rr, -1, 1, false);
  pts.push(`L${x0} ${y0 + rr}`);
  corner(x0 + rr, y0 + rr, -1, -1, true);
  return pts.join(" ") + " Z";
}

let ro = null;

// The border as an SVG data-URI. Painted as the element's OWN background-image, it
// lives in the border box regardless of overflow, transform, filter, or containing
// block — the four things that broke every positioned-child attempt. Stroke centered
// at bw/2 → its outer edge sits on the box edge (== the fill clip), so background and
// border share ONE boundary and land on the pixel grid (crisp sides + corners).
function borderImage(w, h, r, n, bw, color) {
  const d = pathFor(w, h, r, n, bw / 2);
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}' viewBox='0 0 ${w} ${h}'>` +
    `<path d='${d}' fill='none' stroke='${color}' stroke-width='${bw}'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

// (Re)apply the border background-image + the background longhands a React
// `background:` shorthand write would have reset. Guarded so it no-ops in steady state.
function applyBorder(el, w, h, r, bw) {
  const color = el.__sqColor;
  const drawBorder = bw > 0 && color && color !== "transparent";
  const orig = el.__sqOrigBgImg || "";   // the element's own bg-image (e.g. a gradient fill)
  if (!drawBorder) {
    // No border to paint → NEVER touch the element's background. A CSS linear-gradient
    // IS a background-image, so blanking it here wiped gradient fills (gold buttons,
    // the audio progress bar). Only undo a border image if we had added one.
    if (el.__sqBorderOn) { el.__sqBorderOn = false; el.style.backgroundImage = orig; el.__sqBgNorm = el.style.backgroundImage; }
    return;
  }
  // Border image, layered OVER the element's original gradient/fill if it had one.
  const img = borderImage(w, h, r, el.__sqN, bw, color) + (orig ? `, ${orig}` : "");
  const cleared = el.style.backgroundImage !== el.__sqBgNorm;   // React reset our background
  if (el.__sqBorderOn && img === el.__sqBgWanted && !cleared) return;
  el.__sqBorderOn = true;
  el.__sqBgWanted = img;
  el.style.backgroundImage = img;
  el.style.backgroundOrigin = "border-box";
  el.style.backgroundClip = "border-box";
  el.style.backgroundRepeat = "no-repeat";
  el.style.backgroundSize = "100% 100%";
  el.style.backgroundPosition = "0 0";
  el.__sqBgNorm = el.style.backgroundImage;   // browser-normalised readback for the guard
}

// Undo everything and leave the element in its native rounded state (pills/circles).
function release(el) {
  el.__sqManaged = false;
  el.__sqReleased = true;
  el.style.clipPath = "";
  el.style.removeProperty("border-radius");
  if (el.__sqOrigBR) el.style.borderRadius = el.__sqOrigBR;
  if (el.__sqColor) el.style.borderColor = el.__sqColor === "transparent" ? "" : el.__sqColor;
  if (el.__sqBorderOn) {   // only if WE injected a border image — restore the original bg
    el.style.backgroundImage = el.__sqOrigBgImg || "";
    el.style.backgroundSize = ""; el.style.backgroundRepeat = "";
    el.style.backgroundOrigin = ""; el.style.backgroundClip = "";
  }
  if (el.__sqShadow) el.style.filter = "";
  if (ro) ro.unobserve(el);
  el.__sqClip = null; el.__sqBgNorm = null; el.__sqBgWanted = null; el.__sqBorderOn = false;
}

// Geometry (size-dependent): clip the FILL to the superellipse + paint the border image.
function drawGeom(el) {
  if (!el.__sqManaged) return;
  const ow = el.offsetWidth, oh = el.offsetHeight;
  if (!ow || !oh) return;
  // Clip to the element's EXACT (fractional) size so it doesn't sub-pixel-cut the
  // right/bottom border — offsetWidth is rounded DOWN, and on a 1px border a ~0.5px cut
  // erases most of it (the ViewToggle's faint right/bottom edge). Under a CSS transform
  // (e.g. `.press:active` scale) the rect shrinks a lot → fall back to the
  // transform-immune offsetWidth so the clip isn't pinned small and stuck after release.
  // (Fractional clip-path coords look invisible in headless Edge but render on-device.)
  const rect = el.getBoundingClientRect();
  const w = Math.abs(rect.width - ow) < 1.5 ? rect.width : ow;
  const h = Math.abs(rect.height - oh) < 1.5 ? rect.height : oh;
  const minSide = Math.min(w, h);
  // Became a pill/circle → hand it back to the native rounded border.
  if (el.__sqOrig != null && el.__sqOrig * 2 >= minSide - 0.5) { release(el); return; }
  const bw = el.__sqBW || 0;
  const r = Math.min(el.__sqR, minSide / 2);
  const clip = `path("${pathFor(w, h, r, el.__sqN, 0)}")`;
  if (el.__sqClip !== clip) { el.__sqClip = clip; el.style.clipPath = clip; }
  applyBorder(el, w, h, r, bw);
}

// Border props (color/width) + keep the native border blanked + border image applied.
// Runs on every style mutation of a managed element (pre-paint microtask).
function syncBorder(el) {
  if (el.__sqBusy || !el.__sqManaged) return;
  el.__sqBusy = true;
  const cs = getComputedStyle(el);
  // Prefer the INLINE border color (React's TARGET value). getComputedStyle lags
  // during a CSS `transition` — at t=0 it still returns the OLD color, which left a
  // switched tab/filter stuck showing its previous border. When inline is "transparent"
  // that's OUR blanked value → keep the last real color (don't read the fading computed).
  const inlineCol = el.style.borderTopColor;
  if (inlineCol !== "transparent") {
    const col = inlineCol || cs.borderTopColor;
    if (!TRANSPARENT.has(col)) el.__sqColor = col;
  }
  const bw = parseFloat(el.style.borderTopWidth) || parseFloat(cs.borderTopWidth) || 0;
  if (bw > 0) el.__sqBW = bw;
  if (el.style.borderTopColor !== "transparent") el.style.borderColor = "transparent";
  if (el.style.borderRadius !== "0px") el.style.setProperty("border-radius", "0px", "important");
  // clip-path does the rounded clip; drop overflow clipping (it would clip nothing of
  // ours now, but keep it consistent). Never touch scroll containers (auto/scroll).
  if ((cs.overflowX === "hidden" || cs.overflowX === "clip") &&
      (cs.overflowY === "hidden" || cs.overflowY === "clip")) el.style.overflow = "visible";
  if (el.__sqShadow && el.style.boxShadow !== "none") { el.style.boxShadow = "none"; el.style.filter = el.__sqShadow; }
  if (el.__sqNoGlow && el.style.boxShadow !== "none") el.style.boxShadow = "none";
  drawGeom(el);
  el.__sqBusy = false;
}

function attach(el) {
  if (SKIP_TAGS.has(el.tagName) || el.__sqReleased) return;

  if (el.__sq) {                       // already visited
    if (el.__sqManaged) syncBorder(el);
    return;
  }
  el.__sq = true;
  const orig = origRadius(el);
  if (orig == null) { el.__sqManaged = false; return; }      // no rounding / a circle

  // Pill/circle → leave the native rounded border (round icon-buttons stay round).
  const rect = el.getBoundingClientRect();
  const minSide = Math.min(rect.width, rect.height);
  if (minSide > 0 && orig * 2 >= minSide - 0.5) { el.__sqManaged = false; return; }

  const cs = getComputedStyle(el);
  const uniform =
    cs.borderTopWidth === cs.borderRightWidth && cs.borderTopWidth === cs.borderBottomWidth &&
    cs.borderTopWidth === cs.borderLeftWidth &&
    cs.borderTopColor === cs.borderRightColor && cs.borderTopColor === cs.borderBottomColor &&
    cs.borderTopColor === cs.borderLeftColor;
  if (!uniform) { el.__sqManaged = false; return; }          // heterogeneous border → native

  const tok = tokenFor(el);
  el.__sqManaged = true;
  el.__sqOrigBR = el.style.borderRadius;
  el.__sqOrig = orig;
  el.__sqN = tok.n;
  el.__sqR = tok.r;
  el.__sqBW = parseFloat(cs.borderTopWidth) || 0;
  el.__sqColor = TRANSPARENT.has(cs.borderTopColor) ? "transparent" : cs.borderTopColor;
  el.__sqOrigBgImg = (cs.backgroundImage && cs.backgroundImage !== "none") ? cs.backgroundImage : "";

  const bs = cs.boxShadow;
  // Low-end phones: no glow at all (a drop-shadow filter is a GPU pass per element).
  if (LOW_END && bs && bs !== "none" && !bs.includes("inset")) el.__sqNoGlow = true;
  else if (bs && bs !== "none" && !bs.includes("inset")) {
    const m = bs.match(/(rgba?\([^)]+\)|#[0-9a-fA-F]+)\s+(-?[\d.]+)px\s+(-?[\d.]+)px\s+([\d.]+)px/);
    if (m) el.__sqShadow = `drop-shadow(${m[2]}px ${m[3]}px ${Math.round(parseFloat(m[4]) * 0.7)}px ${m[1]})`;
  }

  syncBorder(el);   // blanks native border, applies border image + clip
  ro.observe(el);
}

function scan(root) {
  if (root.nodeType !== 1) return;
  if (root.matches?.("[style*='border-radius']")) attach(root);
  root.querySelectorAll?.("[style*='border-radius']").forEach(attach);
}

export function initSquircles() {
  if (window.__sqInit) return;
  window.__sqInit = true;
  ro = new ResizeObserver(entries => entries.forEach(e => drawGeom(e.target)));
  const mo = new MutationObserver(muts => {
    for (const m of muts) {
      const t = m.target;
      if (m.type === "attributes") {
        if (t.nodeType === 1) attach(t);   // attach() re-syncs managed ones
      } else if (m.type === "childList") {
        m.addedNodes.forEach(scan);
      }
    }
  });
  const start = () => {
    scan(document.body);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });
  };
  if (document.body) start();
  else document.addEventListener("DOMContentLoaded", start);
}
